<template>
  <div class="invite-page-root">
    <vue-recaptcha :sitekey="sitekey"
                   :loadRecaptchaScript="true"
                   size="invisible"
                   @verify="onVerify"
                   @expired="onExpired"
                   ref="captcha"
                   v-if="sitekey"
    />
    <loading-spinner ref="loadingSpinner"/>
    <div class="invite-page-content">
      <transition name="thank-you">
        <div v-if="sendingState === SendState.SENT" id="rsvp-sent-message">
          <p>Thank you for sending your RSVP, we've received it and we look forward to seeing
             you!</p>
        </div>
      </transition>
      <div class="invite-view-rsvp cascade-flex-grow" v-if="sitekey">
        <transition name="form-transition">
          <div class="cascade-flex-grow" v-if="sendingState === SendState.UNSENT">
            <p class="pop">Please ensure the following details are correct:</p>
            <p class="unpop">Note that you can change the name too.</p>

            <div class="invite-row-cards">
              <RSVPPerson v-model="persons[index]"
                          v-for="(person, index) in persons"
                          :key="index"
              />
              <RSVPPerson v-if="addedPlusOne"
                          v-model="plusOne"
              />
            </div>
            <p class="unpop" :style="{visibility: canAddPlusOne ? 'visible' : 'hidden'}">
              <vue-checkbox type="border" v-model="addedPlusOne">
                I would like to bring a +1
              </vue-checkbox>
            </p>
          </div>
        </transition>
        <div class="invite-rsvp-buttons">
          <transition name="send-button">
            <div v-if="sendingState !== SendState.SENT" :class="sendButtonClasses" ref="sendButton">
              <vue-button :disabled="sendButtonDisabled" :type="rsvpType"
                          @click="onSubmit"
              >
                {{sendButtonText}}
              </vue-button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import VueRecaptcha from 'vue-recaptcha'
  import axios from 'axios'
  import RSVPPerson from '../components/RSVPPerson.vue'
  import LoadingSpinner from '../components/LoadingSpinner.vue'

  function delay(ms, v) {
    return new Promise(resolve => setTimeout(resolve.bind(null, v), ms))
  }

  const SendState = {
    UNSENT: 1,
    SENDING: 2,
    SENT: 3,
    SENT_INTERSTITIAL: 4,
    FAILED: 5,
  }

  export default {
    name: 'invitation',
    components: {
      LoadingSpinner,
      RSVPPerson,
      VueRecaptcha,
    },
    data() {
      return {
        persons: [],
        payload: '',
        sitekey: '',
        apiKey: '',
        wantHouse: false,
        canAddPlusOne: false,
        addedPlusOne: false,
        plusOne: {},
        sendingState: SendState.UNSENT,
      }
    },
    methods: {
      async onSubmit() {
        await this.setSending()
        this.$refs.captcha.execute()
      },
      sendRSVP(token) {
        const persons = this.addedPlusOne ? [...this.persons, this.plusOne] : this.persons
        return axios.post(
          `${this.getAPIRoot()}/rsvp`,
          {
            captchaToken: token,
            payload: this.payload,
            persons,
            wantHouse: this.wantHouse,
          },
          { headers: { 'x-api-key': this.apiKey } },
        )
      },
      async onVerify(token) {
        try {
          await this.sendRSVP(token)
          this.sendingState = SendState.SENT_INTERSTITIAL
          await delay(1000)
          this.sendingState = SendState.SENT
        } catch (err) {
          console.log(err)
          this.sendingState = SendState.FAILED
        }
        this.$refs.captcha.reset()
      },
      async setSending() {
        const b = this.$refs.sendButton
        const rect = b.getBoundingClientRect()
        b.style.top = `${rect.top}px`
        b.style.left = `${rect.left}px`
        b.style.position = 'fixed'
        await delay(1)
        b.style.top = ''
        b.style.left = ''
        this.sendingState = SendState.SENDING
      },
      onExpired() {
        this.$refs.captcha.reset()
      },
      async fetchData() {
        const payload = this.$route.query.payload
        const response = await axios.get(
          `${this.getAPIRoot()}/recipient/${payload}`,
        )
        return response.data
      },
      async updateData() {
        const delayEnd = delay(1000)
        this.payload = this.$route.query.payload
        const responseData = await this.fetchData()
        // @todo Respond to errors when invite fetchData fails
        this.persons = responseData.persons
        this.sitekey = responseData.keys.captcha
        this.apiKey = responseData.keys.rsvp
        this.sendingState = responseData.alreadyResponded ? SendState.SENT : SendState.UNSENT
        this.canAddPlusOne = responseData.canAddPlusOne
        await delayEnd
        this.$refs.loadingSpinner.requestStop()
      },
      getAPIRoot() {
        const isLocal = Object.prototype.hasOwnProperty.call(this.$route.query, 'local')
        if (isLocal) {
          const l = window.location
          return `${l.protocol}//${l.hostname}:8000`
        }
        const isDev = Object.prototype.hasOwnProperty.call(this.$route.query, 'dev')
        const stage = isDev ? 'api-dev' : 'api'
        const baseSubdomain = !isDev ? 'q27r7eqju5' : 'el05jtvsuh'
        return `https://${baseSubdomain}.execute-api.us-east-1.amazonaws.com/${stage}`
      },
    },
    computed: {
      pronoun() {
        return this.persons.length > 1 ? 'We' : 'I'
      },
      sendButtonClasses() {
        return {
          'send-button': true,
          'send-button-middle': this.sendingState !== SendState.UNSENT,
        }
      },
      sendButtonDisabled() {
        return [
          SendState.SENDING,
          SendState.SENT_INTERSTITIAL,
          SendState.SENT,
        ].includes(this.sendingState)
      },
      sendButtonText() {
        switch (this.sendingState) {
          case SendState.UNSENT:
            return 'Send RSVP'
          case SendState.SENDING:
            return 'Sending ...'
          case SendState.SENT:
          case SendState.SENT_INTERSTITIAL:
            return 'Sent Successfully'
          case SendState.FAILED:
            return 'Failed to send, retry?'
          default:
            return 'Send RSVP'
        }
      },
      rsvpType() {
        switch (this.sendingState) {
          case SendState.SENT:
          case SendState.SENT_INTERSTITIAL:
            return 'success'
          case SendState.FAILED:
            return 'danger'
          default:
            return null
        }
      },
    },
    watch: {
      $route: 'updateData',
    },
    created() {
      this.SendState = SendState
    },
    mounted() {
      this.updateData()
    },
  }
</script>

<style scoped lang="scss">
  #rsvp-sent-message {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 3em);

    p {
      width: 100%;
    }
  }

  .invite-row-cards {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    align-items: center;
  }

  .invite-view-rsvp {
    align-items: stretch;
    max-width: 96vw;

    p {
      margin: 0.1em;

      &:nth-of-type(2) {
        margin-bottom: 1em;
      }
    }
  }

  .invite-page-root {
    flex-grow: 1;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .invite-page-content {
    width: 100%;
    box-sizing: border-box;
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .send-button {
    width: fit-content;
    transition: top .5s, color .5s;
    margin-left: auto;
    margin-right: auto;
    position: relative;
  }

  .send-button-middle {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
  }

  .form-transition-leave-active {
    transition: all .2s;
  }

  .form-transition-leave-to {
    opacity: 0;
  }

  .thank-you-enter-active, .send-button-leave-active {
    transition-property: opacity, transform, filter, color;
    transition-duration: .5s;
  }

  .thank-you-enter {
    color: white;
    opacity: 0;
    filter: blur(10px);
  }

  .send-button-leave-to {
    opacity: 0;
    color: white;
    filter: blur(10px);
  }
</style>
